import { Component, ViewChild } from '@angular/core';
import { IonicPage, NavController, AlertController, LoadingController, Platform } from 'ionic-angular';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SubscriptionsService } from '../../services/subscriptions/subscriptions-service';
import { CustomersService } from '../../services/customers/customers-service';
import { AuthenticationApi } from '../../services/authentication/authentication-api';
import { CustomerTabsPage } from '../customer-tabs/customer-tabs';
import { ENV } from '../../environments/environment';
import { ScannerPage } from '../scanner/scanner';
import { CustomerLicensePlateExistsValidator } from '../../validators/customer-license-plate-exists';
import { Customer } from '../../models/customer';
import { SubscriptionItem } from '../../models/subscription-item';

@IonicPage()
@Component({ selector: 'page-new-account', templateUrl: 'new-account.html' })

export class NewAccountPage {

  // define embedded view, sliders.
  @ViewChild('signupSlider') signupSlider: any;

  // form controls / validation
  public slideOneForm: FormGroup;
  public slideTwoForm: FormGroup;
  public slideThreeForm: FormGroup;
  public submitAttempt: boolean = false;
  public maxCcExp: string;

  // defined data models.
  public Customer: Customer;

  // data storage vars.
  public objectKeys = Object.keys;
  public isCordova: boolean;
  public vehicleYears: Array<number>;
  public expMonths: Array<number>;
  public expYears: Array<number>;
  public creditCardTypes: Array<any>;
  public subscriptionItems: Array<any>;
  public signupCompleted: boolean;
  public closeQrPreview: boolean;
  public qrCodesEndpoint: string;
  public showCancelBtn: boolean = true;
  public selectOptions: any = { cssClass: null };
  public promoOptions: any = [];
  public userRole: string = null;

  private setVehicleYears() {
    this.vehicleYears = [];
    let maxYear = (new Date()).getFullYear() + 2;
    for(let i = maxYear; i > maxYear-100; i--)
    {
      this.vehicleYears.push(i);
    }
  }

  public setPromoOptions() {
    this.promoOptions = [
      {key: 'none', label: 'None'}, 
      {key: 'skip-first-month', label: 'Free First Month'}, 
      {key: 'skip-prorated-fee', label: 'Free Signup'}, 
    ]
  }

  private setCreditCardTypes() {
    this.creditCardTypes = [
      {
        type: "visa",
        label: "Visa"
      },
      {
        type: "mc",
        label: "Mastercard"
      },
      {
        type: "amex",
        label: "American Express"
      },
      {
        type: "discover",
        label: "Discover"
      },
      {
        type: "diners_club",
        label: "Diners Club"
      },
      {
        type: "jcb",
        label: "JCB"
      }
    ];
  }

  private setCreditCardExpYears() {
    this.expYears = [];
    let minYear = (new Date()).getFullYear() - 10;
    let maxYear = (new Date()).getFullYear() + 10;
    for(let i = maxYear; i >= minYear; i--)
    {
      this.expYears.push(i);
    }
  }

  // Event handler for when the view has
  // completed loading.
  ionViewDidLoad() {

    // available credit card types
    this.setCreditCardTypes();

    // set available vehicle years.
    // available vehicle years.
    this.setVehicleYears();

    // available exp months.
    this.expMonths = [1,2,3,4,5,6,7,8,9,10,11,12];

    // available exp years.
    this.setCreditCardExpYears();

    // set promo options.
    this.setPromoOptions();

    // Lock the slider from being manually
    // swiped.
    this.signupSlider.lockSwipes(true);
    this.showCancelBtn = true;

    // Define custom select classes if
    // not in mobile env.
    this.selectOptions.cssClass = this.isCordova ? null : 'extra-wide';
  }

  toCamelCase(str): string {
    return str.split("_").map(function(word,index){

      // If it is the first word make sure to lowercase all the chars.
      if(index == 0) {
        return String(word).toLowerCase();
      }

      // If it is not the first word only upper case the first char and lowercase the rest.
      return String(word).charAt(0).toUpperCase() + String(word).slice(1).toLowerCase();

    }).join("");
  }

  goToCustomer() {
    let self: NewAccountPage = this;
    let loading = this.loadingCtrl.create({ content: "Loading Customer..." });
    loading.present();
    this.custService.getById(this.Customer.get("meta").ZohoContactId,{_related: ["Events", "Invoices"], _meta: true}).subscribe(
      response => {
        let o: object = {};
        for(var e in response){ o[self.toCamelCase(e)] = response[e]; }
        let Cust: Customer = new Customer(o);
        this.custService.set(Cust);
        loading.dismiss();
        this.navCtrl.push(CustomerTabsPage, {Customer: Cust});
      },
      err => {
        loading.dismiss();
        this.notify("Error",JSON.stringify(err));
      }
    )
  }

  processCarWash() {
    let loading = this.loadingCtrl.create({ content: "Processing Car Wash..." });
    loading.present();
    this.subscriptionService.runIntake(this.Customer.get("meta").PaymentSubscriptionId)
      .subscribe(
        res => {
          loading.dismiss();
          this.notify("Proceed!","Your car wash has been processed.  Enjoy!");
        },
        rej => {
          loading.dismiss();
          this.notify("Caution!","An error occurred processing your car wash.  Please check Clover for a matching order before proceeding.");
        }
      );
  }

  notify(title, msg, buttons?: Array<any>) {

    // mobile device
    if(this.isCordova)
    {
      let notify = this.alertCtrl.create({
        title: title, subTitle: msg, buttons: (buttons ? buttons : ['Ok'])
      });

      return notify.present();
    }

    // desktop browser
    alert(msg);
  }

  // Event handler cancels the current
  // new subscription creation & returns
  // user to home screen (scanner page).
  cancel() {

    if([(this.slideOneForm.dirty?1:0) * 1, (this.slideTwoForm.dirty?1:0) * 1, (this.slideThreeForm.dirty?1:0) * 1].filter(function(a){ return a; }).length)
    {
      let alert = this.alertCtrl.create({
        title: 'Confirm Cancel',
        message: "If you cancel now, you'll lose the information already entered.  Are you sure you want to cancel?",
        buttons: [
          {
            text: 'No, Go Back',
            role: 'cancel'
          },
          {
            text: 'Yes, Cancel',
            handler: () => {
              this.navCtrl.setRoot(ScannerPage);
            }
          }
        ]
      });
      alert.present();
    }

    else
    {
      this.navCtrl.setRoot(ScannerPage);
    }
  }

  // Event handler to validate (then save)
  // the 3 slide forms.
  validate() {

    this.submitAttempt = true;

    // if the `slideOneForm` is invalid, slide back to that view now.
    if(!this.slideOneForm.valid){ return this.signupSlider.slideTo(0); }

    // if the `slideTwoForm` is invalid, slide back to that view now.
    else if(!this.slideTwoForm.valid){ return this.signupSlider.slideTo(1); }

    // if the `slideThreeForm` is invalid, slide back to that view now.
    else if(!this.slideThreeForm.valid){ return this.signupSlider.slideTo(2); }

    this.save();
  }

  // Saves the Customer Subscription
  // data model, creating a new subscription
  // account - Then, updates slides to show
  // success message, customer overview &
  // provide ability to print QR code.
  save() {
    // ---------------------------------------------------------------
    // success. forms are valid.
    // proceed to store the supplied values.
    // ---------------------------------------------------------------
    // 1. engage `loading` alert prompt.
    let loading = this.loadingCtrl.create({ content: 'Creating Account...', dismissOnPageChange: true });
    loading.present();


    // 2. merge all values from the FormControl
    // objects.  convert all object keynames from
    // camel case to snake case.
    var r,
        mergedPayload = {},
        requestPayload = { billing: null, cc_exp: null, cc_exp_month: null, cc_exp_year: null, cc_zip_code: null };
    Object.assign(mergedPayload,this.slideOneForm.value, this.slideTwoForm.value, this.slideThreeForm.value);
    for(r in mergedPayload){
      requestPayload[this.camelToSnake(r)] = mergedPayload[r];
      switch(r)
      {
        case "ccExp":
          requestPayload[this.camelToSnake(r)] = String(requestPayload[this.camelToSnake(r)].split("-")[1])+requestPayload[this.camelToSnake(r)].split("-")[0].substr(2,2)
          break;
      }
    }
    requestPayload.billing = {zip: requestPayload.cc_zip_code};

    // 3. make call to SubscriptionService to save
    // the new record.
    this.subscriptionService
      .createSubscription(requestPayload)
      .subscribe(customerParams => {

        // disable the save button.
        this.showCancelBtn = false;

        // dismiss the loading alert.
        loading.dismiss();

        // instantiate customer data model.
        this.Customer = new Customer(mergedPayload);

        // store the values from the new subscription creation.
        this.Customer.set("meta",customerParams);

        // change view to the 'completed' success state.
        this.signupSlider.lockSwipes(false);
        this.signupSlider.slideTo(this.signupSlider.length()-1);
        this.signupSlider.lockSwipes(true);

      },response => {

        // dismiss the loading alert.
        loading.dismiss();

        try {
          this.alertCtrl.create({ title: "Error", message: response.error.message, buttons: [{text: 'Ok'}] }).present();
        }
        catch(ex) {
          let msg: string = "An error occurred attempting to save the new subscription.  Please check your inputs and try again.";
          this.alertCtrl.create({ title: "Error", message: msg, buttons: [{text: 'Ok'}] }).present();
        }
      });
  }

  // Slide-has-changed event handler.
  slideChanged() {
    if(!this.signupSlider){ return; }
    switch(this.signupSlider.getActiveIndex())
    {
      case 1: case "1":
        if(this.slideOneForm.invalid){ this.signupSlider.slidePrev(); }
        break;

      case 2: case "2":
        if(this.slideTwoForm.invalid){ this.signupSlider.slidePrev(); }
        break;
    }
  }

  // Alert error notification.
  getError(ctrl) {
    return typeof ctrl === 'object' && ctrl.hasOwnProperty("errors") ? (ctrl.errors ? Object.keys(ctrl.errors)[0] : false) : false;
  }

  // Prev/Next slider controls.
  signupSliderCtrl() {
    var self = this;

    return {
      showPrev: function() {
        if(!self.signupSlider){ return false; }
        return self.signupSlider.getActiveIndex() < 3 && self.signupSlider.getActiveIndex() > 0;
      },
      showNext: function() {
        if(!self.signupSlider){ return false; }
        switch(parseInt(self.signupSlider.getActiveIndex()))
        {
          case 0:
            return self.slideOneForm.valid;
          case 1:
            return self.slideTwoForm.valid;
          case 2:
            break;
        }
        return self.signupSlider.getActiveIndex() < 2;
      },
      prev: function() {
        self.showCancelBtn = true;
        self.signupSlider.lockSwipes(false);
        self.signupSlider.slidePrev();
        self.signupSlider.lockSwipes(true);
      },
      next: function() {
        self.signupSlider.lockSwipes(false);
        self.signupSlider.slideNext();
        self.signupSlider.lockSwipes(true);
      }
    }
  }

  // Key-press event handler sanitizes
  // input values for credit card numbers.
  numOnlyMask(keyCode) {
    return [8,48,49,50,51,52,53,54,55,56,57,189].indexOf(parseInt(keyCode)) > -1;
  }

  // Key-press event handler sanitizes
  // input values for license plate numbers.
  licensePlateMask(keyCode) {
    return (/[a-zA-Z0-9]/.test(String.fromCharCode(keyCode)));
  }

  // Determine the role of the user
  // attempting to create a new account.
  setUserRole() {

    // set default role.
    this.userRole = "staff";
 
    // if profile observable succeeds, 
    // attempt to collect role from the 
    // returned User object.
    this.authApi.profile().subscribe(User => {
      try {
        this.userRole = User.roles.filter(function(r){ return r.name.toLowerCase().indexOf("admin") > -1; }) ? "admin" : "staff";
      }catch(ex) {
        console.log("Failed to determine the user role on the 'new-account' page. ",ex);
      } 
    });

  }

  // Load the available Subscription plans.
  fetchSubscriptionItems() {
    this.subscriptionService.getItems().subscribe(items => {
      this.subscriptionItems = items
      .map(function(i){ return new SubscriptionItem(i); })
      .filter(function(i){ return !i.get("hidden"); });
    });
  }

  // Defines all the formbuilder/form control
  // objects for the 3 signup slide forms.
  setupFormControls() {

    this.slideOneForm = this.formBuilder.group({
        firstName: ['', Validators.compose([Validators.minLength(2), Validators.maxLength(40), Validators.pattern(/^[a-zA-Z]*$/), Validators.required])],
        lastName: ['', Validators.compose([Validators.minLength(2), Validators.pattern(/^[a-zA-Z\-]*$/), Validators.required])],
        email: [''],
        phone: ['', Validators.compose([Validators.required])],
        dob: ['']
    });

    this.slideTwoForm = this.formBuilder.group({
        vehicleMake: ['', Validators.compose([Validators.required])],
        vehicleModel: ['', Validators.compose([Validators.required])],
        vehicleYear: ['', Validators.compose([Validators.required])],
        licensePlate: ['',
          Validators.compose([Validators.required]),
          this.licensePlateExistsValidator.checkLicensePlate.bind(this.licensePlateExistsValidator)
        ]
    });

    this.slideThreeForm = this.formBuilder.group({
      planId: ['', Validators.compose([Validators.required])],
      ccType: ['', Validators.compose([Validators.required])],
      ccNumber: ['', Validators.compose([Validators.required, Validators.minLength(14), Validators.maxLength(20)])],
      ccExp: ['',Validators.compose([Validators.required])],
      ccCvc: ['', Validators.compose([Validators.required, Validators.pattern('^[0-9]{3,5}$')])],
      ccZipCode: ['', Validators.compose([Validators.required, Validators.pattern('^[0-9]{5}$')])],
      promo: ['']
    });
  }

  // Converts `camelCase` strings to
  // `snake_case` strings
  camelToSnake(subjVal) {
    var subjectString = subjVal.toString(),
        separator = "_";

    return subjectString.replace(/[A-Z]/g, function(letter, pos) {
            return (pos ? separator : "") + letter.toLowerCase();
    });
  }

  // Prints the QR Code currently displayed
  // on the 'success' page, post new account
  // sign up.
  print(qrCodeFilename: any) {

    if(!qrCodeFilename){ return; }

    let qrDom: string;

    qrDom = "<html style='margin:0px;margin-top:99px;padding:0px;width:210px;height:190px;overflow:hidden;'>"
          +   "<body style='padding:0px;height:190px;'>"
          +		"<div style='height: 30px;text-align:center;'>"
		  +		  "<svg version='1.1' id='Layer_1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' "
		  +		  	"width='180px' height='29.5px' viewBox='0 0 180 29.5' enable-background='new 0 0 180 29.5' xml:space='preserve'>"
		  +		  "<g>"
		  +		  	"<path fill='#010101' d='M1.736,3.938c0-0.856,0.898-1.24,1.796-1.24h9.023c0.855,0,1.197,0.897,1.197,1.752"
		  +		  		"c0,0.984-0.427,1.796-1.197,1.796H5.799v4.405h3.934c0.77,0,1.197,0.77,1.197,1.583c0,0.684-0.385,1.539-1.197,1.539H5.799v6.713"
		  +		  		"c0,0.855-1.027,1.24-2.01,1.24c-0.984,0-2.011-0.428-2.011-1.24V3.938H1.736z M23.117,14.244v6.2c0,0.685-0.855,1.24-1.967,1.24"
		  +		  		"c-1.027,0-1.796-0.556-1.796-1.24v-0.555c-0.941,1.068-2.137,2.095-3.934,2.095c-1.796,0-3.335-1.07-3.335-3.465"
		  +		  		"c0-3.079,2.48-3.933,6.072-3.933h0.941v-0.257c0-0.898-0.471-1.925-1.968-1.925c-2.138,0-2.352,0.855-3.036,0.855"
		  +		  		"c-0.898,0-1.369-1.069-1.369-2.009c0-1.197,2.523-2.095,4.447-2.095C21.45,9.155,23.117,11.635,23.117,14.244z M19.098,16.509"
		  +		  		"H18.97c-1.497,0-2.865,0.128-2.865,1.412c0,0.77,0.428,1.069,1.026,1.069c0.812,0,1.968-1.069,1.968-2.395V16.509L19.098,16.509z"
		  +		  		" M32.781,14.628c0-1.326-0.897-1.968-1.795-1.968c-0.855,0-1.625,0.599-1.625,1.968v5.815c0,0.599-0.94,1.24-2.01,1.24"
		  +		  		"c-1.154,0-2.01-0.599-2.01-1.24v-9.921c0-0.727,0.856-1.197,2.01-1.197c0.983,0,1.796,0.513,1.796,1.197v0.599"
		  +		  		"c0.556-1.026,1.711-1.967,3.421-1.967c1.411,0,2.565,1.069,3.079,2.609c0.898-1.796,2.437-2.609,3.892-2.609"
		  +		  		"c2.437,0,4.746,1.967,4.746,5.474v5.815c0,0.599-0.897,1.24-2.009,1.24c-1.07,0-2.01-0.599-2.01-1.24v-5.815"
		  +		  		"c0-1.326-0.855-1.968-1.753-1.968c-0.855,0-1.667,0.642-1.667,1.968v5.858c0,0.855-1.027,1.198-2.01,1.198"
		  +		  		"c-0.941,0-2.01-0.385-2.01-1.198L32.781,14.628L32.781,14.628z M50.614,5.049c0,0.983-0.941,1.882-2.095,1.882"
		  +		  		"c-1.198,0-2.139-0.899-2.139-1.882c0-0.984,0.941-1.839,2.139-1.839C49.673,3.21,50.614,4.023,50.614,5.049z M50.528,10.523v9.921"
		  +		  		"c0,0.599-0.898,1.24-2.009,1.24c-1.156,0-2.011-0.599-2.011-1.24v-9.921c0-0.727,0.855-1.197,2.011-1.197"
		  +		  		"C49.63,9.326,50.528,9.796,50.528,10.523z M57.114,3.467v17.02c0,0.599-0.898,1.24-2.011,1.24c-1.154,0-2.01-0.598-2.01-1.24V3.467"
		  +		  		"c0-0.727,0.855-1.197,2.01-1.197C56.216,2.227,57.114,2.74,57.114,3.467z M58.696,11.593c-0.085-0.128-0.128-0.343-0.128-0.514"
		  +		  		"c0-1.026,1.668-1.924,2.737-1.924c0.513,0,0.941,0.171,1.112,0.641l2.951,7.056l2.138-7.056c0.171-0.47,0.599-0.641,1.026-0.641"
		  +		  		"c0.941,0,2.78,0.898,2.78,2.01c0,0.129-0.042,0.342-0.085,0.428l-3.72,9.236c-1.326,3.292-1.839,6.542-7.141,6.542"
		  +		  		"c-1.027,0-1.838-0.171-1.838-1.71c0-0.898,0.256-1.668,0.983-1.668c0.94,0,3.72-0.043,3.72-3.335L58.696,11.593L58.696,11.593z'/>"
		  +		  	"<path fill='#010101' d='M84.225,6.204c-1.497,0-2.352,0.855-2.352,2.608v6.885c0,1.796,0.855,2.609,2.352,2.609"
		  +		  		"c2.095,0,2.224-1.583,2.31-2.609c0.085-0.983,0.983-1.197,2.009-1.197c1.411,0,2.053,0.385,2.053,1.924"
		  +		  		"c0,3.465-2.823,5.431-6.543,5.431c-3.421,0-6.243-1.668-6.243-6.158V8.812c0-4.489,2.822-6.157,6.243-6.157"
		  +		  		"c3.721,0,6.543,1.881,6.543,5.174c0,1.54-0.642,1.924-2.011,1.924c-1.068,0-1.966-0.299-2.01-1.197"
		  +		  		"C86.535,7.872,86.449,6.204,84.225,6.204z M102.785,14.244v6.2c0,0.685-0.856,1.24-1.967,1.24c-1.026,0-1.797-0.556-1.797-1.24"
		  +		  		"v-0.555c-0.94,1.068-2.138,2.095-3.934,2.095c-1.795,0-3.335-1.07-3.335-3.465c0-3.079,2.48-3.933,6.072-3.933h0.94v-0.257"
		  +		  		"c0-0.898-0.47-1.925-1.967-1.925c-2.138,0-2.352,0.855-3.036,0.855c-0.898,0-1.368-1.069-1.368-2.009"
		  +		  		"c0-1.197,2.522-2.095,4.447-2.095c4.318,0,5.943,2.48,5.943,5.089H102.785z M98.764,16.509h-0.085"
		  +		  		"c-1.497,0-2.865,0.128-2.865,1.412c0,0.77,0.427,1.069,1.026,1.069c0.812,0,1.967-1.069,1.967-2.395v-0.086H98.764z"
		  +		  		" M111.935,12.661c-1.454,0-2.822,1.498-2.822,3.892v3.892c0,0.599-0.898,1.24-2.053,1.24s-1.967-0.599-1.967-1.24v-9.921"
		  +		  		"c0-0.727,0.855-1.197,1.967-1.197c0.984,0,1.797,0.513,1.797,1.197v0.983c0.512-1.154,1.795-2.352,3.078-2.352h0.77"
		  +		  		"c0.77,0,1.454,0.812,1.454,1.753c0,0.983-0.684,1.796-1.454,1.796L111.935,12.661L111.935,12.661z M137.978,20.486"
		  +		  		"c-0.343,0.898-1.497,1.369-2.651,1.369c-1.155,0-2.437-0.428-2.608-1.369l-1.369-6.157l-1.369,6.157"
		  +		  		"c-0.214,0.898-1.454,1.369-2.609,1.369c-1.197,0-2.394-0.428-2.651-1.369l-5.174-15.864c-0.043-0.086-0.043-0.214-0.043-0.342"
		  +		  		"c0-0.898,1.454-1.625,2.608-1.625c0.599,0,1.155,0.214,1.326,0.77l3.934,13.342l2.053-8.81c0.128-0.684,1.069-1.069,1.967-1.069"
		  +		  		"c0.898,0,1.796,0.385,1.967,1.069l2.053,8.81l3.934-13.342c0.127-0.556,0.684-0.77,1.325-0.77c1.154,0,2.609,0.727,2.609,1.625"
		  +		  		"c0,0.128-0.043,0.256-0.043,0.342L137.978,20.486L137.978,20.486z M153.628,14.244v6.2c0,0.685-0.855,1.24-1.967,1.24"
		  +		  		"c-1.026,0-1.796-0.556-1.796-1.24v-0.555c-0.94,1.068-2.138,2.095-3.934,2.095c-1.796,0-3.336-1.07-3.336-3.465"
		  +		  		"c0-3.079,2.48-3.933,6.073-3.933h0.94v-0.257c0-0.898-0.47-1.925-1.966-1.925c-2.139,0-2.353,0.855-3.036,0.855"
		  +		  		"c-0.899,0-1.37-1.069-1.37-2.009c0-1.197,2.523-2.095,4.448-2.095C152.004,9.155,153.628,11.635,153.628,14.244L153.628,14.244z"
		  +		  		" M149.608,16.509h-0.085c-1.496,0-2.865,0.128-2.865,1.412c0,0.77,0.428,1.069,1.026,1.069c0.812,0,1.967-1.069,1.967-2.395v-0.086"
		  +		  		"H149.608z M161.71,18.092c0-1.796-6.072-0.556-6.072-4.961c0-1.924,1.198-4.02,4.875-4.02c2.266,0,4.404,0.77,4.404,1.882"
		  +		  		"c0,0.512-0.514,1.924-1.454,1.924c-0.684,0-1.497-0.77-2.908-0.77c-1.025,0-1.496,0.427-1.496,0.855"
		  +		  		"c0,1.454,6.158,0.385,6.158,4.96c0,2.438-1.796,4.021-5.003,4.021c-2.737,0-4.961-1.113-4.961-2.395"
		  +		  		"c0-0.599,0.514-1.796,1.326-1.796c0.854,0,1.454,1.155,3.506,1.155C161.454,18.99,161.71,18.477,161.71,18.092z M171.119,20.444"
		  +		  		"c0,0.599-0.941,1.24-2.01,1.24c-1.154,0-2.01-0.599-2.01-1.24V3.467c0-0.727,0.856-1.197,2.01-1.197c1.068,0,2.01,0.514,2.01,1.197"
		  +		  		"v7.698c0.47-1.027,1.625-1.967,3.25-1.967c2.352,0,4.404,2.481,4.404,5.474v5.815c0,0.855-1.026,1.24-2.01,1.24"
		  +		  		"c-0.94,0-2.009-0.428-2.009-1.24v-5.858c0-0.983-0.856-1.968-1.881-1.968c-0.856,0-1.753,0.728-1.753,1.968V20.444L171.119,20.444z"
		  +		  		"'/>"
		  +		  "</g>"
		  +		  "</svg>"
          +		"</div>"
          +     "<div style='margin:0px;margin-left:6px;padding:0px;overflow:hidden;width:180px;height:175px;'>"
          +       `<img src='${ENV.apiUrl}/qr-codes/${qrCodeFilename.replace(".svg",".md.svg")}' `
          +       "style='width:220px;height:220px;margin:0px;margin-left:-20px;margin-top:-20px;padding:0px;' />"
          +     "</div>"
          +   "</body>"
          +  "</html>";

    var printWindow = window.open('', 'Family Car Wash | Subscription QR Code','height=300,width=300');
    printWindow.document.write(qrDom);
    printWindow.document.close();
    printWindow.onload = function() { printWindow.print(); };
  }

  complete() {
    this.signupSlider.lockSwipes(false);
    this.signupSlider.slideTo(this.signupSlider.length()-1);
    this.signupSlider.lockSwipes(true);
  }

  constructor(private navCtrl: NavController,
    private alertCtrl: AlertController,
    private formBuilder: FormBuilder,
    private custService: CustomersService,
    private loadingCtrl: LoadingController,
    private platform: Platform,
    private authApi: AuthenticationApi,
    private subscriptionService: SubscriptionsService,
    private licensePlateExistsValidator: CustomerLicensePlateExistsValidator) {

      this.setUserRole();

      this.fetchSubscriptionItems();

      this.setupFormControls();

      this.qrCodesEndpoint = `${ENV.apiUrl}/qr-codes/`;

      var yr: number = (new Date()).getFullYear();
      this.maxCcExp = `${yr+10}-12-31`;

      this.platform.ready().then(() => {

        // Identify if this is browser or device.
        this.isCordova = platform.is('cordova');

      });
  }
}



// WEBPACK FOOTER //
// ./src/pages/new-account/new-account.ts